import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Button,
  CircularProgress,
  InputBase,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Search, PlayArrow } from '@mui/icons-material';
import useStyles from '../HeygenVoicesDialog/style';
import { spacing, styled } from '@mui/system';
import { ElevenLabsVoice } from '../../../../context/types';
import { THEME_COLORS } from '../../../../context/constants';

const SearchBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#f1f1f1',
  border: `1px solid ${theme.palette.divider}`,
  padding: '2px 10px',
  borderRadius: '14px',
  marginBottom: '10px',
  maxWidth: '300px',
}));

interface ElevenLabsVoicesDialogProps {
  open: boolean; // Whether the modal is open
  onClose: () => void; // Function to handle closing the modal
  elevenLabsVoices: Array<ElevenLabsVoice>; // List of ElevenLabs voices
  onSelectVoice: (voiceId: string) => void; // Function to handle selecting a voice
}

const ElevenLabsVoicesDialog: React.FC<ElevenLabsVoicesDialogProps> = ({
  open,
  onClose,
  elevenLabsVoices,
  onSelectVoice,
}) => {
  const classes = useStyles(); // Access styles
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null); // Track audio instance

  const voicesPerPage = 20;

  // Filter voices based on search query
  const filteredVoices = elevenLabsVoices.filter((voice) => {
    // Only show voices with preview_url available
    if (!voice.preview_url) return false;

    const matchesSearchQuery = JSON.stringify(voice)
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesSearchQuery;
  });

  const totalPages = Math.ceil(filteredVoices.length / voicesPerPage);
  const currentVoices = filteredVoices.slice(
    (currentPage - 1) * voicesPerPage,
    currentPage * voicesPerPage
  );

  const handlePageChange = (direction: string) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === 'previous' && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleSearchChange = (event: any) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleVoiceClick = async (voice: ElevenLabsVoice) => {
    onSelectVoice(voice.voice_id);
    onClose();
  };

  const playAudio = (audioUrl: string) => {
    stopAudio(); // Stop any currently playing audio
    const audio = new Audio(audioUrl);
    audio.play().catch((error) => console.error('Play failed: ', error));
    setCurrentAudio(audio); // Save reference to the currently playing audio
  };

  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0; // Reset audio to the start
      setCurrentAudio(null); // Clear current audio reference
    }
  };

  // Stop audio when modal closes
  React.useEffect(() => {
    if (!open) {
      stopAudio();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className={classes.dialog}>
        <DialogTitle>
          Select an ElevenLabs Voice
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {/* Search Bar */}
              <Grid container sx={{ alignItems: 'center' }}>
                <SearchBar sx={{ marginRight: '4px' }}>
                  <InputBase
                    placeholder="Search voices..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    fullWidth
                  />
                  <IconButton>
                    <Search />
                  </IconButton>
                </SearchBar>
                <Typography
                  variant="body2"
                  style={{ fontSize: '12px', color: THEME_COLORS.darkYellow }}
                >
                  Hint: you can search for voice name...
                </Typography>
              </Grid>

              {/* Voice List */}
              <Grid container spacing={2} sx={{ marginTop: '8px' }}>
                {currentVoices.length === 0 ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" style={{ textAlign: 'center', padding: '20px' }}>
                      No voices found. {searchQuery && 'Try a different search term.'}
                    </Typography>
                  </Grid>
                ) : (
                  currentVoices.map((voice) => (
                    <Grid item xs={12} sm={6} md={4} key={voice.voice_id}>
                      <Tooltip title={voice.name}>
                        <div
                          className={classes.voiceCard}
                          onClick={() => {
                            handleVoiceClick(voice);
                            stopAudio();
                          }}
                        >
                          <div className={classes.voiceInfo}>
                            <span>
                              {voice.name.length > 10 ? `${voice.name.slice(0, 10)}...` : voice.name}
                            </span>
                            <div className={classes.soundWave} />

                            {/* Play Button */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering card click
                                playAudio(voice.preview_url);
                              }}
                            >
                              <PlayArrow />
                            </IconButton>
                          </div>
                        </div>
                      </Tooltip>
                    </Grid>
                  ))
                )}
              </Grid>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className={classes.paginationControls}>
                  <Button
                    variant="contained"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('previous')}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="contained"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default ElevenLabsVoicesDialog;
